/** PointFive Score v1.4 — the algorithm as law (2026-07-02 specification, supersedes v1.3 as-built).
 *
 *  This table is the §16 Metric Reference verbatim: every metric's weight, sigmoid constants, population prior,
 *  direction, shrink unit and minimum observation count. Nothing here is display data — the illustrative values the
 *  demo screen prints live in the dataset, as they did under v1.3.
 *
 *  **What changed, and why it is a new version rather than a calibration.** v1.4 is a correctness release: it repairs
 *  three metrics that were scored backwards or dead (VaR Compliance, Tail Risk, Crisis Resilience), revives two more
 *  (Edge Persistence, Overtrading Control), replaces the spurious equity-curve R² with a scale-free trend statistic,
 *  and re-bands the tiers onto the canonical business thresholds. A score computed under 1.3 and one computed under
 *  1.4 are not comparable, which is exactly why `SCORE_VERSION` is stored on every snapshot and replay refuses to
 *  compare across it.
 *
 *  **The Default Dominance Rule (§4.6) is a property of this table, not of the engine.** Every metric's fallback
 *  value *is* its prior. There is no separate default column because there must not be one: v1.3 had five fallbacks
 *  that beat their own prior, so missing data outscored honest reporting. `spec-v14.test.ts` asserts the rule holds
 *  for every row rather than trusting that nobody adds a sixth.
 */

export const SCORE_VERSION_V14 = '1.4';
/** The constants this spec was tuned with, named separately from the spec itself. The 1.4 document does not carry a
 *  calibration designation, so this one is ours — and it has to differ from `SCORE_VERSION_V14`, because the two
 *  fields exist to answer different questions on a divergent replay: "the rules moved" and "the numbers behind the
 *  rules moved" are separate findings, and a snapshot that stored the same string for both could report neither. */
export const CALIBRATION_VERSION_V14 = 'cal-2026.07-v14';

/* ── §2 System constants ──────────────────────────────────────────────────────────────────────────────────────── */

/** Shrinkage strengths, denominated per observation unit (§4.3, F-14). A 500-trade scalper with three weeks of
 *  history must shrink its Sharpe by DAY count, not trade count — under v1.3 it kept 83% self-weight on fifteen
 *  days of evidence. */
export const BAYESIAN_KAPPA_TRADES = 100;
export const BAYESIAN_KAPPA_DAYS = 60;
export const BAYESIAN_KAPPA_MONTHS = 6;
/** Soft prior blend for regime metrics, in months (§4.4). */
export const REGIME_KAPPA_MONTHS = 3;

/** Exponential decay half-life in months (§4.2). A two-year-old trade contributes ≈0.40 of a fresh one. */
export const TIME_DECAY_HALFLIFE = 18;

export const CONFIDENCE_TRADE_SATURATION = 500;
export const CONFIDENCE_MONTH_SATURATION = 18;

/** The calibration threshold (owner directive 2026-08-27 §8). The same 500 the confidence curve saturates at, and
 *  deliberately the same number rather than a second one: a score becomes *calibrated* at exactly the point its
 *  evidence term stops growing, so "Calibrated" means "the sample is no longer what is holding this back".
 *
 *  **It is a TRADE threshold and only a trade threshold.** The 18-month term still shapes the confidence figure,
 *  but it must not gate calibration: a trader who reaches 500 eligible trades in four months is calibrated, and the
 *  directive says so explicitly. Re-introducing a months gate is a product decision that needs its own approval. */
export const CALIBRATION_TRADE_THRESHOLD = 500;

/** What a score is claiming about its own evidence.
 *
 *  `provisional` — fewer than `CALIBRATION_TRADE_THRESHOLD` eligible trades. The score is real, computed and
 *  displayed; what it does *not* yet carry is a settled confidence result, and it says so with a label rather than
 *  by being reduced. Before the directive a provisional account was silently down-tiered by two bands and shown
 *  "0% confidence" — indistinguishable from a 17-trade account no matter how much evidence it had actually
 *  accumulated, which made the one figure meant to express evidence carry no signal across the entire range a
 *  private beta occupies.
 *
 *  `calibrated` — the threshold is crossed and the ordinary §13.2 confidence rules apply in full. */
export type CalibrationState = 'provisional' | 'calibrated';
export const DD_COMPOSURE_MIN_OBS = 20;

/** Minimum acceptable return, annual — the Omega hurdle (§5.6). At τ=0 Omega was near-duplicate information to
 *  Profit Factor; at τ=MAR it measures asymmetry *above a hurdle*, which PF does not. */
export const MAR_ANNUAL = 0.05;

/** Short-volatility signature thresholds (§13.4). */
export const SHORTVOL_SKEW = -1.0;
export const SHORTVOL_KURT = 5.0;
export const SHORTVOL_WINRATE = 0.7;

/** Capital eligibility gate (§13.3). */
export const CAPITAL_TAILRATIO_MAX = 8.0;
export const CAPITAL_CONFIDENCE_MIN = 60;

/** Cross-pillar modifier bounds (§10). */
export const MODIFIER_CLAMP_V14 = 50;
export const SYNERGY_HIGH = 0.7;
export const SYNERGY_LOW = 0.4;
/** Bonuses require statistical evidence; penalties always apply (§10, asymmetric conservatism). */
export const BONUS_CONFIDENCE_GATE = 50;

/* ── §2 Tier bands (re-banded; resolves the canonical taxonomy conflict) ──────────────────────────────────────── */

export interface TierBand {
  readonly min: number;
  readonly name: string;
  readonly percentile: string;
  readonly meaning: string;
}

/** Highest first. The former "Average 420" reference was a population mean, not a tier boundary, and is retired
 *  from tier language. */
export const PF_TIERS_V14: readonly TierBand[] = [
  { min: 920, name: 'Exceptional', percentile: 'Top 0.5%', meaning: 'Flagship capital allocation' },
  {
    min: 800,
    name: 'Elite',
    percentile: 'Top 1–2%',
    meaning: 'Capital-eligible (subject to the §13.3 gate)',
  },
  { min: 650, name: 'Professional', percentile: 'Top 15%', meaning: 'Investor-ready' },
  { min: 500, name: 'Competent', percentile: 'Top 30%', meaning: 'Building verified record' },
  { min: 350, name: 'Developing', percentile: 'Top 60%', meaning: 'Early record' },
  { min: 0, name: 'Novice', percentile: 'Bottom 40%', meaning: 'Insufficient demonstrated edge' },
];

/* ── §16 Metric reference ─────────────────────────────────────────────────────────────────────────────────────── */

/** Which unit a metric's evidence is counted in — and therefore which effective sample size and which κ its
 *  shrinkage uses (§4.3). Getting this wrong is F-14, and it is invisible until a scalper's three weeks of data
 *  score like two years of it. */
export type ShrinkUnit = 'trades' | 'days' | 'months' | 'oosDays' | 'none';

/** How a metric's raw value becomes points. `sigmoid` is the default; `square` is the two metrics the spec scores
 *  as p², `log` is Track Record Depth, and `none` marks the binary Bear Market Bonus. */
export type ScoreShape = 'sigmoid' | 'square' | 'log' | 'binary';

export interface MetricSpecV14 {
  readonly key: string;
  readonly name: string;
  readonly pillar: string;
  readonly max: number;
  /** Sigmoid steepness and midpoint. Null for the non-sigmoid shapes. */
  readonly k: number | null;
  readonly m: number | null;
  /** The population prior — and, per §4.6, the value a metric takes when it cannot be computed. */
  readonly prior: number | null;
  readonly better: 'higher' | 'lower';
  readonly shrinkUnit: ShrinkUnit;
  /** Minimum observations, in the metric's own unit, before it is computed rather than defaulted. */
  readonly minObs: number;
  readonly shape: ScoreShape;
  /** Value ceiling applied before shrinkage, where the spec caps one. */
  readonly cap: number | null;
}

const met = (
  key: string,
  name: string,
  pillar: string,
  max: number,
  k: number | null,
  m: number | null,
  prior: number | null,
  better: 'higher' | 'lower',
  shrinkUnit: ShrinkUnit,
  minObs: number,
  shape: ScoreShape = 'sigmoid',
  cap: number | null = null,
): MetricSpecV14 => ({ key, name, pillar, max, k, m, prior, better, shrinkUnit, minObs, shape, cap });

export const METRICS_V14: readonly MetricSpecV14[] = [
  // P1 — Risk-Adjusted Returns (250)
  met('sharpe', 'Sharpe Ratio', 'rar', 80, 1.5, 1.0, 0.2, 'higher', 'days', 30),
  met('sortino', 'Sortino Ratio', 'rar', 60, 1.2, 1.5, 0.35, 'higher', 'days', 30, 'sigmoid', 10),
  met('calmar', 'Calmar Ratio', 'rar', 50, 1.0, 1.0, 0.5, 'higher', 'days', 30, 'sigmoid', 10),
  met('alpha', 'Alpha Generation', 'rar', 40, 0.15, 0.05, 0.02, 'higher', 'days', 60),
  met('informationRatio', 'Information Ratio', 'rar', 15, 2.0, 0.5, 0.2, 'higher', 'days', 60),
  met('omega', 'Omega Ratio', 'rar', 15, 1.5, 1.4, 1.0, 'higher', 'days', 30, 'sigmoid', 10),

  // P2 — Consistency & Reliability (250)
  met('trendConsistency', 'Trend Consistency', 'cr', 80, 1.2, 1.2, 0.6, 'higher', 'days', 20, 'sigmoid', 5),
  met('winRateStability', 'Win Rate Stability', 'cr', 50, 5.0, 0.08, 0.15, 'lower', 'trades', 20),
  met('profitFactor', 'Profit Factor', 'cr', 40, 2.0, 1.5, 1.2, 'higher', 'trades', 20, 'sigmoid', 5),
  met('positiveMonths', 'Positive Months Ratio', 'cr', 30, null, null, 0.55, 'higher', 'months', 6, 'square'),
  met('skew', 'Return Skewness', 'cr', 15, 1.5, 0.3, 0.0, 'higher', 'days', 20),
  met('kurtosis', 'Return Kurtosis', 'cr', 15, 0.5, 3.0, 3.0, 'lower', 'days', 20),
  met('edgePersistence', 'Edge Persistence', 'cr', 20, 3.0, 0.0, 0.0, 'higher', 'days', 60),

  // P3 — Risk Management (200)
  met('drawdownControl', 'Drawdown Control', 'rm', 60, 8.0, 0.15, 0.2, 'lower', 'trades', 20),
  met('recoverySpeed', 'Recovery Speed', 'rm', 30, 0.05, 30.0, 45.0, 'lower', 'trades', 10),
  met('positionSizing', 'Position Sizing Discipline', 'rm', 30, 5.0, 0.15, 0.2, 'lower', 'trades', 10),
  met(
    'stopLossAdherence',
    'Stop-Loss Adherence',
    'rm',
    25,
    null,
    null,
    0.8,
    'higher',
    'trades',
    10,
    'square',
  ),
  met('varBreach', 'Out-of-Sample VaR Breach Test', 'rm', 20, 1.2, 1.5, 0.5, 'lower', 'oosDays', 20),
  met('tailRisk', 'Tail Risk Behaviour', 'rm', 20, 80, 0.03, 0.03, 'lower', 'days', 10),
  met('crisisResilience', 'Crisis Resilience', 'rm', 15, 3.0, 0.3, 0.6, 'lower', 'days', 20),

  // P4 — Operational Maturity (150)
  met('trackRecord', 'Track Record Depth', 'om', 40, null, null, null, 'higher', 'none', 3, 'log'),
  met(
    'statisticalSignificance',
    'Statistical Significance',
    'om',
    35,
    0.005,
    200,
    null,
    'higher',
    'none',
    20,
  ),
  met('regimeAdaptability', 'Regime Adaptability', 'om', 25, 3.0, 0.5, 0.5, 'higher', 'months', 6),
  met('bearMarketBonus', 'Bear Market Bonus', 'om', 10, null, null, null, 'higher', 'none', 0, 'binary'),
  met('executionQuality', 'Execution Quality', 'om', 20, 0.3, 5.0, 8.0, 'lower', 'trades', 10),
  met('frequencyConsistency', 'Frequency Consistency', 'om', 10, 5.0, 0.15, 0.25, 'lower', 'trades', 20),

  // P5 — Behavioral Integrity (150)
  met('revengeResistance', 'Revenge Trading Resistance', 'bi', 25, 8.0, 0.1, 0.1, 'lower', 'trades', 10),
  met('tiltResistance', 'Tilt Resistance', 'bi', 25, 5.0, 0.15, 0.15, 'lower', 'trades', 15),
  met('overtradingControl', 'Overtrading Control', 'bi', 20, 6.0, 0.15, 0.1, 'lower', 'trades', 20),
  met('dispositionEffect', 'Disposition Effect Absence', 'bi', 20, 5.0, 0.15, 0.15, 'lower', 'trades', 10),
  met('convictionSizing', 'Conviction Sizing', 'bi', 20, 3.0, 0.3, 0.2, 'higher', 'trades', 15),
  met(
    'drawdownComposure',
    'Drawdown Composure',
    'bi',
    20,
    2.5,
    0.08,
    0.08,
    'lower',
    'trades',
    DD_COMPOSURE_MIN_OBS,
  ),
  met('planAdherence', 'Plan Adherence', 'bi', 20, null, null, 0.75, 'higher', 'trades', 10, 'square'),
];

export interface PillarSpecV14 {
  readonly id: string;
  readonly name: string;
  readonly max: number;
}

/** Each pillar's maximum is the SUM OF ITS OWN METRICS, derived rather than declared.
 *
 *  The specification states both, and they disagree twice: §5 heads Pillar 1 "(250 pts)" while its six metrics sum
 *  to 260, and §8 heads Pillar 4 "(150 pts)" while its six sum to 140. The two errors cancel, so §11's total of 1000
 *  is correct either way and the discrepancy is invisible in the arithmetic that document checks.
 *
 *  It is not invisible here. §10's modifiers are computed on each pillar's *share* of its own maximum, so a declared
 *  maximum of 250 against 260 points of metrics lets a pillar score 104% — and a synergy threshold of 0.70 would
 *  then be reachable on 0.67 of the real weight. Deriving the maximum makes the share definitionally ≤ 1 and keeps
 *  the total at 1000. Raised with the owner as part of ESC-009; if the intended fix is to move ten points from
 *  Pillar 1 to Pillar 4, changing the two metric weights here is the one-line edit and this comment can go. */
export const PILLARS_V14: readonly PillarSpecV14[] = [
  { id: 'rar', name: 'Risk-Adjusted Returns', max: 0 },
  { id: 'cr', name: 'Consistency & Reliability', max: 0 },
  { id: 'rm', name: 'Risk Management', max: 0 },
  { id: 'om', name: 'Operational Maturity', max: 0 },
  { id: 'bi', name: 'Behavioral Integrity', max: 0 },
].map((pillar) => ({
  ...pillar,
  max: METRICS_V14.filter((metric) => metric.pillar === pillar.id).reduce((sum, m) => sum + m.max, 0),
}));

export const MAX_SCORE_V14 = 1000;

export type MetricKeyV14 = (typeof METRICS_V14)[number]['key'];
